// src/components/Notifications/NotificationBell.jsx
//
// R-NOTIFY §5.5 (N2) — the bell in the top bar, beside the cart, for the
// facility selected in the facility console. The badge is the caller's
// OWN unread count (the server filters on the recipient); hidden at 0,
// "9+" above 9; the cart's `nav-cart-count` dot is the badge precedent.
// Clicking it opens the list page (`/facilities/:id/notifications`), where
// a row is marked read as it is opened — the bell itself is a link, not a
// dropdown, so it owns no layer.
//
// WHEN THE COUNT IS READ: on mount, on every route change, when the
// window regains focus, when the list page says it changed (the
// `NOTIFICATIONS_CHANGED` window event), and every 60 seconds — never
// faster (no socket). The navbar draws its icons twice (the desktop row
// and the phone row), so the count is read ONCE, by `useUnreadNotifications`
// in the navbar, and handed to both bells.
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { getUnreadNotificationCount } from '../../services/api';

/** Fired by the list page after a read, so the badge follows at once. */
export const NOTIFICATIONS_CHANGED = 'frogger:notifications-changed';
/** The slowest the badge ever lags — once a minute, no faster. */
export const UNREAD_POLL_MS = 60000;

/** The badge's text: '' at 0 (no badge), the number to 9, then "9+". */
export const badgeText = (n) => {
  if (!n || n < 1) return '';
  return n > 9 ? '9+' : String(n);
};

export function useUnreadNotifications(facilityId) {
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!facilityId) {
      setUnread(0);
      return undefined;
    }
    let live = true;
    Promise.resolve()
      .then(() => getUnreadNotificationCount(facilityId))
      .then((data) => {
        if (live && data && typeof data.unread === 'number') setUnread(data.unread);
      })
      // A failed read keeps the last count; the next tick tries again.
      .catch(() => {});
    return () => { live = false; };
  }, [facilityId, pathname, tick]);

  useEffect(() => {
    if (!facilityId) return undefined;
    const bump = () => setTick((t) => t + 1);
    const timer = setInterval(bump, UNREAD_POLL_MS);
    window.addEventListener('focus', bump);
    window.addEventListener(NOTIFICATIONS_CHANGED, bump);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', bump);
      window.removeEventListener(NOTIFICATIONS_CHANGED, bump);
    };
  }, [facilityId]);

  return unread;
}

export default function NotificationBell({ facilityId, unread }) {
  const navigate = useNavigate();
  if (!facilityId) return null;
  const text = badgeText(unread);
  const label = text ? `Notifications, ${unread} unread` : 'Notifications';
  return (
    <button
      type="button"
      className="ff-btn ff-btn--ghost ff-btn--icon position-relative me-2 nav-icon-btn"
      onClick={() => navigate(`/facilities/${facilityId}/notifications`)}
      aria-label={label}
      data-tooltip="Notifications"
      data-notification-bell="1"
    >
      <Bell size={18} />
      {text && (
        <span className="nav-cart-count" data-notification-count={unread} aria-hidden="true">
          {text}
        </span>
      )}
    </button>
  );
}

NotificationBell.propTypes = {
  facilityId: PropTypes.string,
  unread: PropTypes.number,
};
